/**
 * KorgMS20Panel - Interface du Korg MS-20
 * 2 VCOs, double filtre (HPF + LPF), 2 enveloppes
 * Thème gris-bleu foncé avec knobs blancs/crème
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { MS20_PRESETS } from '../../state/InstrumentPresets.js';

export class KorgMS20Panel {
    constructor(ms20, store, sequencer) {
        this.ms20 = ms20;
        this.store = store;
        this.sequencer = sequencer;
        this.panelId = 'ms20';

        this.components = {
            knobs: {},
            selects: {},
            stepButtons: []
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('ms20');

        // Restaurer l'état power du synthé
        this.ms20.setPower(isPowered);

        container.innerHTML = `
            <div class="machine ms20 ${isCollapsed ? 'collapsed' : ''} ${!isPowered ? 'powered-off' : ''}" id="ms20_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="ms20_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    KORG <span class="brand">MS-20</span>
                    <div id="ms20_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="ms20_power" title="Power ON/OFF">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="ms20_content">
                <div class="ms20-sections">
                    <!-- VCOs Section -->
                    <div class="ms20-section vco-section">
                        <div class="section-title">VCO</div>
                        <div class="vco-row">
                            <div class="vco-group">
                                <div class="vco-label">VCO 1</div>
                                <div class="vco-controls" id="ms20_vco1_controls"></div>
                            </div>
                            <div class="vco-group">
                                <div class="vco-label">VCO 2</div>
                                <div class="vco-controls" id="ms20_vco2_controls"></div>
                            </div>
                            <div class="vco-group ring-group">
                                <div class="vco-label">RING</div>
                                <div class="vco-controls" id="ms20_ring_controls"></div>
                            </div>
                            <div class="vco-group noise-group">
                                <div class="vco-label">NOISE</div>
                                <div class="vco-controls" id="ms20_noise_controls"></div>
                            </div>
                        </div>
                    </div>

                    <!-- HPF Section (unique au MS-20!) -->
                    <div class="ms20-section hpf-section">
                        <div class="section-title">HIGH PASS FILTER</div>
                        <div class="filter-controls" id="ms20_hpf_controls"></div>
                    </div>

                    <!-- LPF Section (le filtre qui crie!) -->
                    <div class="ms20-section lpf-section">
                        <div class="section-title">LOW PASS FILTER</div>
                        <div class="filter-controls" id="ms20_lpf_controls"></div>
                    </div>

                    <!-- Modulation Generator -->
                    <div class="ms20-section mg-section">
                        <div class="section-title">MG (LFO)</div>
                        <div class="mg-controls" id="ms20_mg_controls"></div>
                    </div>

                    <!-- Envelopes -->
                    <div class="ms20-section envelopes-section">
                        <div class="env-group">
                            <div class="section-title">EG1 FILTER</div>
                            <div class="env-controls" id="ms20_eg1_controls"></div>
                        </div>
                        <div class="env-group">
                            <div class="section-title">EG2 AMP</div>
                            <div class="env-controls" id="ms20_eg2_controls"></div>
                        </div>
                    </div>

                    <!-- Master -->
                    <div class="ms20-section master-section">
                        <div class="section-title">MASTER</div>
                        <div class="master-controls" id="ms20_master_controls"></div>
                    </div>
                </div>

                <!-- Sequencer -->
                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${Array.from({length: 16}, (_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="ms20_gates"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="ms20_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createVCOControls();
            this.createHPFControls();
            this.createLPFControls();
            this.createMGControls();
            this.createEnvelopeControls();
            this.createMasterControls();
            this.createSequencer();
            this.setupPresetSelector();
            this._loadParams();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('ms20_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('ms20_power');
        const machine = document.getElementById('ms20_machine');

        powerBtn.addEventListener('click', () => {
            const isOn = this.ms20.getPower();
            const newState = !isOn;
            this.ms20.setPower(newState);
            this.store.setPowerState('ms20', newState);

            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    setupPresetSelector() {
        const container = document.getElementById('ms20_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: MS20_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.ms20.setParams(presetData);
        this._updateControlsFromParams();
        this._saveParams();
    }

    _updateControlsFromParams() {
        const params = this.ms20.getParams();

        // Update waveform selectors
        ['vco1', 'vco2'].forEach((vco) => {
            const selector = this.components.selects[`${vco}Wave`];
            if (selector && params[`${vco}Waveform`]) {
                selector.setValue(params[`${vco}Waveform`]);
            }
        });

        // Update knobs
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined && knob?.setValue) {
                knob.setValue(params[key]);
            }
        });
    }

    createVCOControls() {
        const params = this.ms20.getParams();

        // VCO 1
        const vco1Container = document.getElementById('ms20_vco1_controls');
        this._createVCOGroup(vco1Container, 1, params);

        // VCO 2
        const vco2Container = document.getElementById('ms20_vco2_controls');
        this._createVCOGroup(vco2Container, 2, params);

        // Ring Modulator
        const ringContainer = document.getElementById('ms20_ring_controls');
        this.components.knobs.ringModLevel = themeManager.createKnob(ringContainer, {
            min: 0, max: 1, value: params.ringModLevel,
            label: 'Level', step: 0.01
        }).onChange(value => {
            this.ms20.setRingModLevel(value);
            this._saveParams();
        });

        // Noise
        const noiseContainer = document.getElementById('ms20_noise_controls');
        this.components.knobs.noiseLevel = themeManager.createKnob(noiseContainer, {
            min: 0, max: 1, value: params.noiseLevel,
            label: 'Level', step: 0.01
        }).onChange(value => {
            this.ms20.setNoiseLevel(value);
            this._saveParams();
        });
    }

    _createVCOGroup(container, vcoNum, params) {
        const prefix = `vco${vcoNum}`;

        // Waveform selector avec boutons
        this.components.selects[`${prefix}Wave`] = new WaveSelector(container, {
            waveforms: [
                { value: 'sawtooth', label: 'SAW' },
                { value: 'square', label: 'PLS' },
                { value: 'triangle', label: 'TRI' }
            ],
            value: params[`${prefix}Waveform`],
            label: 'Wave',
            idPrefix: `ms20_${prefix}`
        }).onChange(value => {
            this.ms20[`setVco${vcoNum}Waveform`](value);
            this._saveParams();
        });

        // Octave
        this.components.knobs[`${prefix}Octave`] = themeManager.createKnob(container, {
            min: -2, max: 2, value: params[`${prefix}Octave`],
            label: 'Octave', step: 1
        }).onChange(value => {
            this.ms20[`setVco${vcoNum}Octave`](Math.round(value));
            this._saveParams();
        });

        // VCO2 gets detune
        if (vcoNum === 2) {
            this.components.knobs.vco2Detune = themeManager.createKnob(container, {
                min: -100, max: 100, value: params.vco2Detune,
                label: 'Detune', step: 1
            }).onChange(value => {
                this.ms20.setVco2Detune(value);
                this._saveParams();
            });
        }

        // Level
        this.components.knobs[`${prefix}Level`] = themeManager.createKnob(container, {
            min: 0, max: 1, value: params[`${prefix}Level`],
            label: 'Level', step: 0.01
        }).onChange(value => {
            this.ms20[`setVco${vcoNum}Level`](value);
            this._saveParams();
        });
    }

    createHPFControls() {
        const container = document.getElementById('ms20_hpf_controls');
        const params = this.ms20.getParams();

        // HPF Cutoff
        this.components.knobs.hpfCutoff = themeManager.createKnob(container, {
            min: 20, max: 10000, value: params.hpfCutoff,
            label: 'Cutoff', step: 1
        }).onChange(value => {
            this.ms20.setHpfCutoff(value);
            this._saveParams();
        });

        // HPF Resonance (Peak)
        this.components.knobs.hpfResonance = themeManager.createKnob(container, {
            min: 0, max: 20, value: params.hpfResonance,
            label: 'Peak', step: 0.1
        }).onChange(value => {
            this.ms20.setHpfResonance(value);
            this._saveParams();
        });
    }

    createLPFControls() {
        const container = document.getElementById('ms20_lpf_controls');
        const params = this.ms20.getParams();

        // LPF Cutoff
        this.components.knobs.lpfCutoff = themeManager.createKnob(container, {
            min: 20, max: 20000, value: params.lpfCutoff,
            label: 'Cutoff', step: 1
        }).onChange(value => {
            this.ms20.setLpfCutoff(value);
            this._saveParams();
        });

        // LPF Resonance (Peak) - C'est celui qui crie!
        this.components.knobs.lpfResonance = themeManager.createKnob(container, {
            min: 0.5, max: 30, value: params.lpfResonance,
            label: 'Peak', step: 0.1
        }).onChange(value => {
            this.ms20.setLpfResonance(value);
            this._saveParams();
        });

        // Env Amount
        this.components.knobs.lpfEnvAmount = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.lpfEnvAmount,
            label: 'EG1 Amt', step: 0.01
        }).onChange(value => {
            this.ms20.setLpfEnvAmount(value);
            this._saveParams();
        });

        // Key Track
        this.components.knobs.lpfKeyTrack = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.lpfKeyTrack,
            label: 'Key Trk', step: 0.01
        }).onChange(value => {
            this.ms20.setLpfKeyTrack(value);
            this._saveParams();
        });
    }

    createMGControls() {
        const container = document.getElementById('ms20_mg_controls');
        const params = this.ms20.getParams();

        // MG Rate
        this.components.knobs.mgRate = themeManager.createKnob(container, {
            min: 0.1, max: 30, value: params.mgRate,
            label: 'Rate', step: 0.1
        }).onChange(value => {
            this.ms20.setMgRate(value);
            this._saveParams();
        });

        // Pitch Mod Amount
        this.components.knobs.mgPitchAmount = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.mgPitchAmount,
            label: 'Pitch', step: 0.01
        }).onChange(value => {
            this.ms20.setMgPitchAmount(value);
            this._saveParams();
        });

        // Filter Mod Amount
        this.components.knobs.mgFilterAmount = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.mgFilterAmount,
            label: 'Filter', step: 0.01
        }).onChange(value => {
            this.ms20.setMgFilterAmount(value);
            this._saveParams();
        });
    }

    createEnvelopeControls() {
        const params = this.ms20.getParams();

        // EG1 (Filter Envelope)
        const eg1Container = document.getElementById('ms20_eg1_controls');

        this.components.knobs.eg1Attack = themeManager.createKnob(eg1Container, {
            min: 0.001, max: 2, value: params.eg1Attack,
            label: 'Attack', step: 0.001
        }).onChange(value => {
            this.ms20.setEg1Attack(value);
            this._saveParams();
        });

        this.components.knobs.eg1Decay = themeManager.createKnob(eg1Container, {
            min: 0.01, max: 3, value: params.eg1Decay,
            label: 'Decay', step: 0.01
        }).onChange(value => {
            this.ms20.setEg1Decay(value);
            this._saveParams();
        });

        this.components.knobs.eg1Sustain = themeManager.createKnob(eg1Container, {
            min: 0, max: 1, value: params.eg1Sustain,
            label: 'Sustain', step: 0.01
        }).onChange(value => {
            this.ms20.setEg1Sustain(value);
            this._saveParams();
        });

        this.components.knobs.eg1Release = themeManager.createKnob(eg1Container, {
            min: 0.01, max: 3, value: params.eg1Release,
            label: 'Release', step: 0.01
        }).onChange(value => {
            this.ms20.setEg1Release(value);
            this._saveParams();
        });

        // EG2 (Amp Envelope)
        const eg2Container = document.getElementById('ms20_eg2_controls');

        this.components.knobs.eg2Attack = themeManager.createKnob(eg2Container, {
            min: 0.001, max: 2, value: params.eg2Attack,
            label: 'Attack', step: 0.001
        }).onChange(value => {
            this.ms20.setEg2Attack(value);
            this._saveParams();
        });

        this.components.knobs.eg2Decay = themeManager.createKnob(eg2Container, {
            min: 0.01, max: 3, value: params.eg2Decay,
            label: 'Decay', step: 0.01
        }).onChange(value => {
            this.ms20.setEg2Decay(value);
            this._saveParams();
        });

        this.components.knobs.eg2Sustain = themeManager.createKnob(eg2Container, {
            min: 0, max: 1, value: params.eg2Sustain,
            label: 'Sustain', step: 0.01
        }).onChange(value => {
            this.ms20.setEg2Sustain(value);
            this._saveParams();
        });

        this.components.knobs.eg2Release = themeManager.createKnob(eg2Container, {
            min: 0.01, max: 3, value: params.eg2Release,
            label: 'Release', step: 0.01
        }).onChange(value => {
            this.ms20.setEg2Release(value);
            this._saveParams();
        });
    }

    createMasterControls() {
        const container = document.getElementById('ms20_master_controls');
        const params = this.ms20.getParams();

        // Portamento
        this.components.knobs.portamento = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.portamento,
            label: 'Porta', step: 0.01
        }).onChange(value => {
            this.ms20.setPortamento(value);
            this._saveParams();
        });

        // Volume
        this.components.knobs.volume = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.volume,
            label: 'Volume', step: 0.01
        }).onChange(value => {
            this.ms20.setVolume(value);
            this._saveParams();
        });
    }

    createSequencer() {
        const notesContainer = document.getElementById('ms20_notes');
        const gatesContainer = document.getElementById('ms20_gates');

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.ms20Notes ? this.store.patterns.ms20Notes[i] : 'C2',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('ms20Notes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.ms20Gates ? this.store.patterns.ms20Gates[i] : false
            }).onChange(active => {
                this.store.setStep('ms20Gates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    _saveParams() {
        if (this.store) {
            this.store.setSynthParams('ms20', this.ms20.getParams());
        }
    }

    _loadParams() {
        const saved = this.store?.getSynthParams('ms20');
        if (saved) {
            this.ms20.setParams(saved);
            this._updateControlsFromParams();
        }
    }

    updateFromParams() {
        const params = this.ms20.getParams();
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined && knob) {
                knob.setValue(params[key]);
            }
        });
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy());
        Object.values(this.components.selects).forEach(s => s?.destroy());
        this.components.stepButtons.forEach(b => b?.destroy());
    }
}
